/**
 * Choosing a related record — a client on a project, a project on a ticket, an
 * employee to assign to.
 *
 * A native `<select>` cannot do this. The list is a register with thousands of
 * rows, so the options have to be **searched on the server**, and the control
 * has to keep showing the current value's label while the search box holds
 * something else entirely.
 *
 * The design decisions worth knowing:
 *
 * * **The label of the selected record is fetched separately** when the value
 *   arrives without one. An edit form opens holding a `public_id` and nothing
 *   else, and a picker that shows the raw id until somebody types is a picker
 *   that looks broken.
 * * **Search is debounced and the endpoint is asked for one small page.** The
 *   list endpoints already implement `q`, so there is no dedicated lookup API
 *   to keep in step — the picker searches exactly what the register searches.
 * * **Clearing is explicit.** An optional relationship is cleared with a button
 *   that says so, not by emptying the text and hoping.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Search, X } from 'lucide-react';

import { api } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { useDebounced } from '../../lib/hooks';
import { Field, type FieldShellProps } from './input';
import { Popover } from './overlays';
import { Spinner } from './surfaces';
import { ScrollArea } from './scroll-area';

export interface RecordOption {
  value: string;
  label: string;
  /** A second line — a code, a client name, whatever disambiguates two rows. */
  detail?: string | null;
}

export interface RecordSelectProps extends Omit<FieldShellProps, 'children'> {
  /** The list endpoint to search, e.g. `/crm/clients`. */
  endpoint: string;
  /** The selected record's `public_id`, or `null`. */
  value: string | null;
  onChange: (value: string | null, option: RecordOption | null) => void;
  /** Turns one row of the endpoint into an option. */
  toOption: (row: Record<string, unknown>) => RecordOption;
  /** Extra query parameters — a status filter, a parent id. */
  params?: Record<string, string | number | boolean | undefined>;
  placeholder?: string;
  /** Allows the empty choice. An optional relationship; the default. */
  clearable?: boolean;
  disabled?: boolean;
  /**
   * The label for the current value, when the caller already knows it.
   *
   * Supplying it skips the resolve query — an edit form that loaded the record
   * usually has the name right there, and a second round trip for a string it
   * is already holding is waste the user waits for.
   */
  valueLabel?: string | null;
}

interface PageResponse {
  rows: Record<string, unknown>[];
  total: number;
}

export function RecordSelect({
  endpoint,
  value,
  onChange,
  toOption,
  params,
  placeholder = 'Search…',
  clearable = true,
  disabled,
  valueLabel,
  label,
  required,
  hint,
  error,
  className,
}: RecordSelectProps) {
  const id = useId();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 250);

  /** The label to show on the closed control, whatever its source. */
  const [selectedLabel, setSelectedLabel] = useState<string | null>(valueLabel ?? null);

  useEffect(() => {
    // A supplied label wins, including when the value is null — a caller that
    // holds only the label ("Sam Fernandes", assignee id not returned by the
    // endpoint) still gets a picker that shows who is assigned.
    if (valueLabel !== undefined && valueLabel !== null) {
      setSelectedLabel(valueLabel);
      return;
    }
    if (value === null) setSelectedLabel(null);
  }, [valueLabel, value]);

  const search = useQuery({
    queryKey: ['record-select', endpoint, debounced, params],
    queryFn: () =>
      api.page<Record<string, unknown>>(endpoint, {
        params: { page_size: 20, q: debounced || undefined, ...params },
      }) as Promise<PageResponse>,
    // Only while the list is on screen. A picker in a closed dialog must not
    // be querying the server.
    enabled: open,
    staleTime: 30_000,
  });

  /**
   * Resolve the label for a value that arrived without one.
   *
   * The **detail** endpoint, by id — not a filtered list. `public_id` is not in
   * any register's filter whitelist, so a filtered list would have its filter
   * silently dropped and return whatever happened to be first: a picker
   * confidently showing the wrong record, which is worse than showing none.
   *
   * Not every endpoint has a detail route, so a failure here is expected and
   * resolves to nothing rather than an error. The way to be certain is to pass
   * `valueLabel`, which every edit form is already holding.
   */
  const resolve = useQuery({
    queryKey: ['record-select-resolve', endpoint, value],
    queryFn: () =>
      api
        .get<Record<string, unknown>>(`${endpoint}/${value}`)
        .catch(() => null),
    enabled: value !== null && selectedLabel === null,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (resolve.data) setSelectedLabel(toOption(resolve.data).label);
    // `toOption` is a prop and usually an inline arrow, so it is deliberately
    // not a dependency — including it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolve.data]);

  const options = useMemo(
    () => (search.data?.rows ?? []).map(toOption),
    // Same reasoning as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search.data],
  );

  const choose = (option: RecordOption) => {
    setSelectedLabel(option.label);
    onChange(option.value, option);
    setOpen(false);
    setTerm('');
  };

  return (
    <Field
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      htmlFor={id}
    >
      <div className="relative">
        <button
          id={id}
          ref={anchorRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((was) => !was)}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-control border bg-surface px-2.5 text-left text-sm transition',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
            'disabled:cursor-not-allowed disabled:opacity-60',
            error ? 'border-danger' : 'border-line hover:border-line-strong',
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              selectedLabel ? 'text-body' : 'text-faint',
            )}
          >
            {selectedLabel ?? placeholder}
          </span>
          {clearable && value !== null && !disabled ? (
            <span
              // A span with a click handler rather than a nested `<button>`:
              // a button inside a button is invalid HTML and browsers recover
              // from it by dropping one of them.
              role="button"
              tabIndex={-1}
              aria-label="Clear"
              className="rounded p-0.5 text-faint hover:bg-surface-2 hover:text-body"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedLabel(null);
                onChange(null, null);
              }}
            >
              <X className="size-3.5" />
            </span>
          ) : null}
          <ChevronDown className="size-3.5 shrink-0 text-faint" aria-hidden />
        </button>

        <Popover
          open={open}
          onClose={() => setOpen(false)}
          anchorRef={anchorRef}
          className="w-(--anchor-width) min-w-64 p-0"
          label={label ?? 'Choose a record'}
        >
          <div className="border-b border-line p-1.5">
            <div className="flex items-center gap-1.5 rounded-control bg-surface-2 px-2">
              <Search className="size-3.5 shrink-0 text-faint" aria-hidden />
              <input
                ref={searchRef}
                autoFocus
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Search…"
                className="h-8 w-full bg-transparent text-sm outline-none placeholder:text-faint"
              />
              {search.isFetching ? <Spinner className="size-3.5" /> : null}
            </div>
          </div>

          <ScrollArea className="max-h-64"><ul className="py-1" role="listbox">
            {options.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-faint">
                {search.isLoading ? (
                  'Looking…'
                ) : search.isError ? (
                  // Not "nothing to choose from": the list was never read.
                  <span className="text-danger">
                    The search could not be run.{' '}
                    <button type="button" className="underline" onClick={() => void search.refetch()}>
                      Try again
                    </button>
                  </span>
                ) : term ? (
                  'Nothing matches that.'
                ) : (
                  'Nothing to choose from yet.'
                )}
              </li>
            ) : (
              options.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => choose(option)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-2"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body">{option.label}</span>
                      {option.detail ? (
                        <span className="block truncate text-2xs text-faint">
                          {option.detail}
                        </span>
                      ) : null}
                    </span>
                    {option.value === value ? (
                      <Check className="size-3.5 shrink-0 text-accent" aria-hidden />
                    ) : null}
                  </button>
                </li>
              ))
            )}
          </ul></ScrollArea>

          {search.data && search.data.total > options.length ? (
            <p className="border-t border-line px-3 py-1.5 text-2xs text-faint">
              Showing {options.length} of {search.data.total}. Type to narrow it.
            </p>
          ) : null}
        </Popover>
      </div>
    </Field>
  );
}
